/**
 * ThreeLayerArchitectureSection.jsx — Phase U (Landing Architecture Animation).
 *
 * Vertical stack of the SCOPE → CODA → TRACE story. Each layer:
 *   • reveals sequentially on scroll-into-view (framer-motion +
 *     IntersectionObserver, 300ms / 600ms delays),
 *   • lands with a subtle scale-in + label pulse,
 *   • is joined to the next layer by a vertical SVG connector that
 *     "draws" (stroke-dashoffset animation) as each layer lands,
 *   • carries an ambient "signal-pulse" dot that travels top→bottom
 *     through the connectors every 4.5s once the layers are all in,
 *   • has a soft breathing micro-motion (~2px y-drift, 3s sine loop)
 *     for the "alive" feel,
 *   • tilts up to 3° toward the cursor on hover (desktop only),
 *   • reveals one extra line of detail on hover (150ms fade).
 *
 * The five-step "How a decision flows" diagram below animates the
 * same way — sequenced left→right with arrow draw-ins, plus an
 * ambient dot that travels through the row every 5.5s.
 *
 * Every ambient loop and micro-motion is gated on `useReducedMotion`.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useInView, useReducedMotion, AnimatePresence } from "framer-motion";
import { Layers, Network, GitBranch, FileText, MessageSquare, Sparkles, Atom, Radio } from "lucide-react";

/* ─────────────────────────────────────────────────────────
 * Data — the three architecture layers + the five flow steps.
 * ────────────────────────────────────────────────────── */
const LAYERS = [
    {
        id: "scope", name: "Interface", title: "Interface", Icon: Layers,
        blurb: "You type a decision. We shape it into a spec.",
        detail: "Interface — product surface · decision intake · audit-ready views",
        bullets: ["Product surface", "Decision intake", "Audit-ready views"],
    },
    {
        id: "coda", name: "Tomauro", title: "Orchestration", Icon: Radio,
        blurb: "Auth, audit, sharing, calibration. The trust surface.",
        detail: "Tomauro — orchestration · seven-layer flow · calibration ledger",
        bullets: ["Seven-layer flow", "Calibration ledger", "Trust surface"],
    },
    {
        id: "trace", name: "TRACE", title: "Engine", Icon: Atom,
        blurb: "Deterministic structural analysis. Reproducible per seed.",
        detail: "TRACE — engine · structural analysis core · deterministic per seed",
        bullets: ["Structural analysis", "Reproducible per seed", "Grounded in priors"],
    },
];

const FLOW = [
    { id: "text",          label: "Decision text",   Icon: MessageSquare },
    { id: "spec",          label: "Structured spec", Icon: FileText      },
    { id: "engine",        label: "Engine",          Icon: GitBranch     },
    { id: "distributions", label: "Distributions",   Icon: Network       },
    { id: "brief",         label: "Executive brief", Icon: Sparkles      },
];

// Timing (ms)
const LAYER_DELAY_STEP = 300;
const LAYER_DURATION   = 620;
const CONNECTOR_DURATION = 400;
const FLOW_DELAY_STEP  = 400;
const AMBIENT_PULSE_MS = 4500;
const AMBIENT_FLOW_MS  = 5500;

/* ─────────────────────────────────────────────────────────
 * A hook: detect if the pointer is fine (mouse) vs coarse (touch).
 * We skip cursor-tilt on touch devices.
 * ────────────────────────────────────────────────────── */
function useFinePointer() {
    const [fine, setFine] = useState(false);
    useEffect(() => {
        if (typeof window === "undefined") return;
        const mq = window.matchMedia("(pointer: fine)");
        setFine(mq.matches);
        const onChange = () => setFine(mq.matches);
        mq.addEventListener?.("change", onChange);
        return () => mq.removeEventListener?.("change", onChange);
    }, []);
    return fine;
}

/* ─────────────────────────────────────────────────────────
 * LayerCard — a single architecture layer with reveal + breathing +
 * cursor-tilt + hover detail.
 * ────────────────────────────────────────────────────── */
function LayerCard({ layer, index, inView, reduce, finePointer }) {
    const revealDelay = index * LAYER_DELAY_STEP / 1000;
    const [hovered, setHovered] = useState(false);
    const [tilt, setTilt] = useState({ x: 0, y: 0 });
    const [pulseKey, setPulseKey] = useState(0);
    const cardRef = useRef(null);
    const { Icon } = layer;

    // Kick the label pulse ~200ms after the card lands.
    useEffect(() => {
        if (!inView || reduce) return;
        const t = setTimeout(() => setPulseKey((k) => k + 1),
                             revealDelay * 1000 + LAYER_DURATION - 200);
        return () => clearTimeout(t);
    }, [inView, reduce, revealDelay]);

    const onMouseMove = (e) => {
        if (!finePointer || reduce) return;
        const rect = cardRef.current?.getBoundingClientRect();
        if (!rect) return;
        // range: [-1, +1] on each axis
        const nx = ((e.clientX - rect.left) / rect.width  - 0.5) * 2;
        const ny = ((e.clientY - rect.top)  / rect.height - 0.5) * 2;
        // Cap at 3 degrees
        setTilt({ x: -ny * 3, y:  nx * 3 });
    };
    const onMouseLeave = () => {
        setHovered(false);
        setTilt({ x: 0, y: 0 });
    };

    return (
        <motion.div
            ref={cardRef}
            data-testid={`architecture-layer-${layer.id}`}
            initial={reduce ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 24, scale: 0.98 }}
            animate={
                inView
                    ? {
                          opacity: 1,
                          y: 0,
                          scale: 1,
                          rotateX: tilt.x,
                          rotateY: tilt.y,
                      }
                    : { opacity: 0, y: 24, scale: 0.98, rotateX: 0, rotateY: 0 }
            }
            transition={{
                opacity:  { delay: revealDelay, duration: LAYER_DURATION / 1000, ease: [0.22, 1, 0.36, 1] },
                y:        { delay: revealDelay, duration: LAYER_DURATION / 1000, ease: [0.22, 1, 0.36, 1] },
                scale:    { delay: revealDelay, duration: LAYER_DURATION / 1000, ease: [0.22, 1, 0.36, 1] },
                rotateX:  { type: "spring", stiffness: 160, damping: 18 },
                rotateY:  { type: "spring", stiffness: 160, damping: 18 },
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
            className="relative bg-surface border border-border rounded-lg p-6 md:p-7 shadow-sm"
            style={{
                perspective: 900,
                transformStyle: "preserve-3d",
                willChange: "transform, opacity",
            }}
        >
            {/* Big decorative layer number sitting behind the card content
                as a background accent. Uses the layer index so no glyph
                asset ships. Muted to ~8% opacity so it never competes
                with the copy. */}
            <div
                aria-hidden
                data-testid={`architecture-layer-${layer.id}-number`}
                className="pointer-events-none absolute select-none font-heading font-bold leading-none"
                style={{
                    right: 24,
                    top: 12,
                    fontSize: 120,
                    color: "#1A237E",
                    opacity: 0.08,
                    letterSpacing: "-0.04em",
                }}
            >
                {String(index + 1).padStart(2, "0")}
            </div>

            {/* Breathing micro-motion — very subtle 2px drift. Independent
                of the reveal state so it starts once landed. */}
            <motion.div
                animate={
                    inView && !reduce
                        ? { y: [0, -2, 0] }
                        : { y: 0 }
                }
                transition={
                    inView && !reduce
                        ? { duration: 3.4, repeat: Infinity, ease: "easeInOut", delay: revealDelay + LAYER_DURATION / 1000 }
                        : { duration: 0 }
                }
                className="relative flex flex-col md:flex-row md:items-start md:justify-between gap-6"
            >
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                        <span className="text-[10.5px] font-mono uppercase tracking-widest text-brand">
                            Layer {index + 1}
                        </span>
                        <motion.div
                            key={`pulse-${pulseKey}`}
                            initial={{ scale: 1 }}
                            animate={reduce ? {} : { scale: [1, 1.14, 1] }}
                            transition={{ duration: 0.55, ease: "easeOut" }}
                            className="inline-flex items-center gap-2"
                        >
                            <Icon size={16} className="text-brand" strokeWidth={1.8} />
                            <span className="font-heading text-[28px] md:text-[32px] font-semibold text-ink-1 tracking-tight leading-none">
                                {layer.name}
                            </span>
                        </motion.div>
                    </div>
                    <div className="mt-1 text-[11.5px] font-mono uppercase tracking-widest text-ink-2">
                        {layer.title}
                    </div>
                    <p className="mt-3 text-[14.5px] text-ink-2 leading-relaxed max-w-xl">
                        {layer.blurb}
                    </p>
                </div>

                {/* Right column — always-visible 3-bullet capability list.
                    Replaces the previous right-side SVG glyph. Bullets
                    render in a tidy indigo-dot column so cards read
                    "capability, capability, capability" at a glance. */}
                <ul
                    className="shrink-0 md:pl-6 md:border-l md:border-border/60 space-y-2"
                    data-testid={`architecture-layer-${layer.id}-bullets`}
                >
                    {(layer.bullets || []).map((b) => (
                        <li key={b} className="flex items-start gap-2.5 text-[13px] text-ink-1">
                            <span
                                aria-hidden
                                className="mt-[7px] w-1.5 h-1.5 rounded-full shrink-0"
                                style={{ background: "#1A237E" }}
                            />
                            <span>{b}</span>
                        </li>
                    ))}
                </ul>
            </motion.div>
        </motion.div>
    );
}

/* ─────────────────────────────────────────────────────────
 * LayerGlyph — per-layer animated SVG. All glyphs share the
 * same viewBox footprint so the LayerCard layout stays stable.
 * Motion is skipped under prefers-reduced-motion.
 * ────────────────────────────────────────────────────── */
function LayerGlyph({ id, inView, reduce, revealDelay }) {
    // Glyphs kick in ~200ms after the layer card lands so the two
    // motions don't compete for attention on entry.
    const start = (revealDelay || 0) + 0.2;
    if (id === "scope")  return <ScopeGlyph  inView={inView} reduce={reduce} start={start} />;
    if (id === "coda")   return <CodaGlyph   inView={inView} reduce={reduce} start={start} />;
    if (id === "trace")  return <TraceGlyph  inView={inView} reduce={reduce} start={start} />;
    return null;
}

// SCOPE — dashboard bars. 5 vertical bars with staggered breathing heights.
function ScopeGlyph({ inView, reduce, start }) {
    const bars = [
        { x:  10, base: 34, peak: 24, delay: 0.00 },
        { x:  36, base: 22, peak: 10, delay: 0.08 },
        { x:  62, base: 38, peak: 28, delay: 0.16 },
        { x:  88, base: 18, peak:  8, delay: 0.24 },
        { x: 114, base: 30, peak: 20, delay: 0.32 },
    ];
    return (
        <svg viewBox="0 0 140 66" className="w-full max-w-[260px] h-16 shrink-0" aria-hidden data-testid="architecture-glyph-scope">
            <defs>
                <linearGradient id="scope-bar-grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"  stopColor="#1A237E" stopOpacity="0.9" />
                    <stop offset="100%" stopColor="#1A237E" stopOpacity="0.55" />
                </linearGradient>
            </defs>
            <line x1="4" y1="56" x2="130" y2="56" stroke="#C5CAE9" strokeWidth="1" />
            {bars.map((b, i) => (
                <motion.rect
                    key={i}
                    x={b.x}
                    width="18"
                    rx="2"
                    fill="url(#scope-bar-grad)"
                    initial={reduce ? { y: 56 - b.base, height: b.base } : { y: 56, height: 0 }}
                    animate={inView ? { y: 56 - b.peak, height: b.peak } : { y: 56, height: 0 }}
                    transition={{
                        duration: 0.6,
                        delay: reduce ? 0 : start + b.delay,
                        ease: [0.22, 1, 0.36, 1],
                    }}
                />
            ))}
            {!reduce && inView && (
                <motion.circle
                    cx="123" cy="12" r="2.5" fill="#1A237E"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 1, 1, 0] }}
                    transition={{ duration: 2.4, repeat: Infinity, delay: start + 1.0, times: [0, 0.15, 0.6, 1] }}
                />
            )}
        </svg>
    );
}

// CODA — orbit + central pulse. Center circle, 4 outer nodes, connectors.
function CodaGlyph({ inView, reduce, start }) {
    const cx = 70, cy = 33;
    const nodes = [
        { x: 70, y:  8 },
        { x: 116, y: 24 },
        { x:  96, y: 56 },
        { x:  44, y: 56 },
        { x:  24, y: 24 },
    ];
    return (
        <svg viewBox="0 0 140 66" className="w-full max-w-[260px] h-16 shrink-0" aria-hidden data-testid="architecture-glyph-coda">
            {nodes.map((n, i) => (
                <motion.line
                    key={`l${i}`}
                    x1={cx} y1={cy} x2={n.x} y2={n.y}
                    stroke="#1A237E" strokeOpacity="0.35" strokeWidth="1" strokeLinecap="round"
                    initial={reduce ? { pathLength: 1, opacity: 0.35 } : { pathLength: 0, opacity: 0 }}
                    animate={inView ? { pathLength: 1, opacity: 0.35 } : { pathLength: 0, opacity: 0 }}
                    transition={{ duration: 0.6, delay: reduce ? 0 : start + i * 0.08, ease: "easeOut" }}
                />
            ))}
            {nodes.map((n, i) => (
                <motion.circle
                    key={`n${i}`}
                    cx={n.x} cy={n.y} r="3"
                    fill="#1A237E"
                    initial={reduce ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
                    animate={inView ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
                    transition={{ duration: 0.35, delay: reduce ? 0 : start + 0.2 + i * 0.07, ease: "backOut" }}
                />
            ))}
            {/* Central pulsing hub */}
            <circle cx={cx} cy={cy} r="6" fill="#FFFFFF" stroke="#1A237E" strokeWidth="1.5" />
            {!reduce && inView && (
                <motion.circle
                    cx={cx} cy={cy} r="6"
                    fill="none" stroke="#1A237E"
                    initial={{ r: 6, opacity: 0.5 }}
                    animate={{ r: [6, 16], opacity: [0.5, 0] }}
                    transition={{ duration: 2.0, repeat: Infinity, delay: start + 0.8, ease: "easeOut" }}
                />
            )}
            <circle cx={cx} cy={cy} r="2.5" fill="#1A237E" />
        </svg>
    );
}

// TRACE — nested cone arcs widening left→right with subtle shimmer.
function TraceGlyph({ inView, reduce, start }) {
    // Three arc-bands that fan out from the left. Each is a filled
    // quadrilateral (SVG path) so we get the "cone widening" effect.
    const bands = [
        { path: "M 12 33 L 128 12 L 128 20 L 12 33 Z", fill: "#D1FAE5", delay: 0.00 },
        { path: "M 12 33 L 128 20 L 128 46 L 12 33 Z", fill: "#EEF0F7", delay: 0.10 },
        { path: "M 12 33 L 128 46 L 128 54 L 12 33 Z", fill: "#FEE2E2", delay: 0.20 },
    ];
    return (
        <svg viewBox="0 0 140 66" className="w-full max-w-[260px] h-16 shrink-0" aria-hidden data-testid="architecture-glyph-trace">
            {bands.map((b, i) => (
                <motion.path
                    key={i}
                    d={b.path}
                    fill={b.fill}
                    initial={reduce ? { opacity: 0.9 } : { opacity: 0, x: -20 }}
                    animate={inView ? { opacity: 0.9, x: 0 } : { opacity: 0, x: -20 }}
                    transition={{ duration: 0.55, delay: reduce ? 0 : start + b.delay, ease: [0.22, 1, 0.36, 1] }}
                />
            ))}
            {/* Median line */}
            <motion.line
                x1="12" y1="33" x2="128" y2="33"
                stroke="#1A237E" strokeWidth="1.8" strokeLinecap="round"
                initial={reduce ? { pathLength: 1 } : { pathLength: 0 }}
                animate={inView ? { pathLength: 1 } : { pathLength: 0 }}
                transition={{ duration: 0.7, delay: reduce ? 0 : start + 0.3, ease: "easeOut" }}
            />
            {/* Origin dot */}
            <circle cx="12" cy="33" r="3" fill="#1A237E" />
            {/* Shimmer sweep */}
            {!reduce && inView && (
                <motion.rect
                    y="6" width="8" height="54" fill="url(#trace-shimmer)"
                    initial={{ x: 8, opacity: 0 }}
                    animate={{ x: [8, 128], opacity: [0, 0.7, 0] }}
                    transition={{ duration: 1.8, repeat: Infinity, delay: start + 1.2, ease: "easeInOut", repeatDelay: 2.0 }}
                />
            )}
            <defs>
                <linearGradient id="trace-shimmer" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0" />
                    <stop offset="50%" stopColor="#FFFFFF" stopOpacity="0.7" />
                    <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
                </linearGradient>
            </defs>
        </svg>
    );
}

/* ─────────────────────────────────────────────────────────
 * Connector — vertical SVG line between two layers with a
 * stroke-dashoffset draw-in and an ambient signal-pulse dot.
 * ────────────────────────────────────────────────────── */
function ArchitectureConnector({ index, inView, reduce }) {
    // Line draws AFTER the layer above lands, so at
    // delay = (index+1)*LAYER_DELAY_STEP + LAYER_DURATION - CONNECTOR_DURATION
    const drawDelay = (index * LAYER_DELAY_STEP + LAYER_DURATION * 0.5) / 1000;
    const height = 46;
    return (
        <div
            className="relative flex justify-center"
            style={{ height, marginTop: -1, marginBottom: -1 }}
            data-testid={`architecture-connector-${index}`}
            aria-hidden
        >
            <svg width="16" height={height} viewBox={`0 0 16 ${height}`} className="overflow-visible">
                <motion.line
                    x1="8" y1="2" x2="8" y2={height - 2}
                    stroke="#1A237E" strokeWidth="1.2" strokeDasharray={height} strokeLinecap="round"
                    initial={reduce ? { strokeDashoffset: 0 } : { strokeDashoffset: height }}
                    animate={inView ? { strokeDashoffset: 0 } : { strokeDashoffset: height }}
                    transition={{ duration: CONNECTOR_DURATION / 1000, delay: reduce ? 0 : drawDelay, ease: "easeOut" }}
                />
                {inView && !reduce && (
                    <motion.circle
                        r="2.4" cx="8" fill="#1A237E"
                        initial={{ cy: 2, opacity: 0 }}
                        animate={{ cy: [2, height - 2], opacity: [0, 1, 1, 0] }}
                        transition={{
                            duration: 0.9,
                            times: [0, 0.1, 0.9, 1],
                            ease: "easeIn",
                            repeat: Infinity,
                            repeatDelay: AMBIENT_PULSE_MS / 1000 - 0.9,
                            delay: drawDelay + (CONNECTOR_DURATION / 1000) + index * 0.15,
                        }}
                    />
                )}
            </svg>
        </div>
    );
}

/* ─────────────────────────────────────────────────────────
 * FlowStep — one card in the "How a decision flows" row.
 * ────────────────────────────────────────────────────── */
function FlowStep({ step, index, inView, reduce, finePointer }) {
    const revealDelay = index * FLOW_DELAY_STEP / 1000;
    const [tilt, setTilt] = useState({ x: 0, y: 0 });
    const [bouncedKey, setBouncedKey] = useState(0);
    const cardRef = useRef(null);
    const { Icon } = step;

    useEffect(() => {
        if (!inView || reduce) return;
        // Trigger icon bounce a touch after the card lands.
        const t = setTimeout(() => setBouncedKey((k) => k + 1),
                             revealDelay * 1000 + 320);
        return () => clearTimeout(t);
    }, [inView, reduce, revealDelay]);

    const onMouseMove = (e) => {
        if (!finePointer || reduce) return;
        const rect = cardRef.current?.getBoundingClientRect();
        if (!rect) return;
        const nx = ((e.clientX - rect.left) / rect.width  - 0.5) * 2;
        const ny = ((e.clientY - rect.top)  / rect.height - 0.5) * 2;
        setTilt({ x: -ny * 2.5, y: nx * 2.5 });
    };

    return (
        <motion.li
            ref={cardRef}
            data-testid={`architecture-flow-${step.id}`}
            initial={reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
            animate={inView ? { opacity: 1, y: 0, rotateX: tilt.x, rotateY: tilt.y } : {}}
            transition={{
                opacity: { delay: revealDelay, duration: 0.5, ease: [0.22, 1, 0.36, 1] },
                y:       { delay: revealDelay, duration: 0.5, ease: [0.22, 1, 0.36, 1] },
                rotateX: { type: "spring", stiffness: 150, damping: 16 },
                rotateY: { type: "spring", stiffness: 150, damping: 16 },
            }}
            onMouseMove={onMouseMove}
            onMouseLeave={() => setTilt({ x: 0, y: 0 })}
            style={{ perspective: 800, transformStyle: "preserve-3d", willChange: "transform, opacity" }}
            className="relative flex flex-col items-start gap-2 p-4 bg-panel border border-border rounded-md"
        >
            <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-brand text-white font-mono text-[10.5px] flex items-center justify-center font-bold">
                    {index + 1}
                </span>
                <motion.span
                    key={`bounce-${bouncedKey}`}
                    initial={{ scale: 1 }}
                    animate={reduce ? {} : { scale: [1, 1.22, 1] }}
                    transition={{ duration: 0.24, ease: "easeOut" }}
                    className="inline-flex"
                >
                    <Icon size={14} className="text-brand" strokeWidth={1.8} />
                </motion.span>
            </div>
            <div className="text-[13px] font-medium text-ink-1 leading-tight">{step.label}</div>
        </motion.li>
    );
}

/* ─────────────────────────────────────────────────────────
 * FlowArrows — SVG overlay with 4 arrows that draw between the
 * 5 steps as they land, plus an ambient dot that travels the row.
 * Positioned absolute over the ol; we compute each arrow's target
 * using CSS grid columns so we don't have to hard-code widths.
 * ────────────────────────────────────────────────────── */
function FlowArrows({ inView, reduce, count }) {
    // Each arrow lives in a grid column that starts at (n) and spans 1,
    // positioned relative to the parent grid. We use a simple flexbox
    // trick: absolutely-positioned SVG overlay with 4 evenly-spaced
    // arrows sitting on the horizontal midline of the row.
    const arrows = new Array(count - 1).fill(0);
    return (
        <div
            aria-hidden
            className="hidden md:flex absolute inset-0 pointer-events-none items-center"
            data-testid="architecture-flow-arrows"
        >
            {arrows.map((_, i) => {
                // The arrow lives at the RIGHT edge of column i (0-indexed).
                // Column right = (i+1)/count * 100% of the grid.
                const rightPct = ((i + 1) / count) * 100;
                const drawDelay = ((i + 1) * FLOW_DELAY_STEP - 220) / 1000;
                return (
                    <svg
                        key={i}
                        style={{ position: "absolute", left: `calc(${rightPct}% - 14px)`, top: "50%", transform: "translateY(-50%)" }}
                        width="28" height="14" viewBox="0 0 28 14" className="overflow-visible"
                    >
                        <motion.line
                            x1="1" y1="7" x2="21" y2="7"
                            stroke="#1A237E" strokeWidth="1.4" strokeLinecap="round" strokeDasharray="20"
                            initial={reduce ? { strokeDashoffset: 0 } : { strokeDashoffset: 20 }}
                            animate={inView ? { strokeDashoffset: 0 } : { strokeDashoffset: 20 }}
                            transition={{ duration: 0.32, delay: reduce ? 0 : drawDelay, ease: "easeOut" }}
                        />
                        <motion.polyline
                            points="17,3 22,7 17,11" fill="none"
                            stroke="#1A237E" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"
                            initial={reduce ? { opacity: 1 } : { opacity: 0 }}
                            animate={inView ? { opacity: 1 } : { opacity: 0 }}
                            transition={{ duration: 0.18, delay: reduce ? 0 : drawDelay + 0.28 }}
                        />
                    </svg>
                );
            })}
            {/* Ambient flowing dot (only after all steps landed) */}
            {!reduce && inView && (
                <motion.div
                    data-testid="architecture-flow-ambient-dot"
                    className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-brand"
                    style={{ boxShadow: "0 0 8px rgba(26,35,126,0.55)" }}
                    initial={{ left: "3%", opacity: 0 }}
                    animate={{ left: ["3%", "97%"], opacity: [0, 1, 1, 0] }}
                    transition={{
                        duration: 2.2,
                        times: [0, 0.06, 0.94, 1],
                        ease: "easeInOut",
                        repeat: Infinity,
                        repeatDelay: (AMBIENT_FLOW_MS - 2200) / 1000,
                        delay: (FLOW.length * FLOW_DELAY_STEP + 400) / 1000,
                    }}
                />
            )}
        </div>
    );
}

/* ═════════════════════════════════════════════════════════
 * Top-level section.
 * ══════════════════════════════════════════════════════ */
export default function ThreeLayerArchitectureSection() {
    const reduce = useReducedMotion();
    const finePointer = useFinePointer();
    const archRef = useRef(null);
    const flowRef = useRef(null);
    const archInView = useInView(archRef, { once: true, amount: 0.25 });
    const flowInView = useInView(flowRef, { once: true, amount: 0.25 });

    // Memoised layer / flow lists so we don't recompute on every render.
    const layers = useMemo(() => LAYERS, []);
    const flow   = useMemo(() => FLOW,   []);

    return (
        <section
            className="relative px-6 py-24 bg-paper reveal"
            data-testid="section-architecture"
        >
            <div className="max-w-content mx-auto">
                {/* Header */}
                <div className="mb-14 text-center">
                    <div className="eyebrow mb-2">Architecture</div>
                    <h2 className="font-heading text-[34px] sm:text-[42px] leading-tight tracking-tight text-ink-1 font-semibold">
                        Three layers, one contract.
                    </h2>
                    <p className="mt-3 text-[15px] text-ink-2 max-w-xl mx-auto leading-relaxed">
                        Natural language at the edges, structural analysis at the core, cryptographic audit binding the two.
                    </p>
                </div>

                {/* Vertical layer stack + connectors */}
                <div ref={archRef} className="max-w-3xl mx-auto" data-testid="architecture-layers">
                    {layers.map((layer, i) => (
                        <div key={layer.id}>
                            <LayerCard
                                layer={layer}
                                index={i}
                                inView={archInView}
                                reduce={reduce}
                                finePointer={finePointer}
                            />
                            {i < layers.length - 1 && (
                                <ArchitectureConnector index={i} inView={archInView} reduce={reduce} />
                            )}
                        </div>
                    ))}
                </div>

                {/* Divider */}
                <div className="mt-16 mb-10 flex items-center gap-3" aria-hidden>
                    <div className="h-px flex-1 bg-border" />
                    <span className="text-[10.5px] font-mono uppercase tracking-widest text-ink-2">How a decision flows</span>
                    <div className="h-px flex-1 bg-border" />
                </div>

                {/* Flow row */}
                <div ref={flowRef} className="relative" data-testid="architecture-flow">
                    <ol className="grid grid-cols-2 md:grid-cols-5 gap-4 relative">
                        {flow.map((step, i) => (
                            <FlowStep
                                key={step.id}
                                step={step}
                                index={i}
                                inView={flowInView}
                                reduce={reduce}
                                finePointer={finePointer}
                            />
                        ))}
                    </ol>
                    <FlowArrows inView={flowInView} reduce={reduce} count={flow.length} />
                </div>
            </div>
        </section>
    );
}
